import { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { FiChevronDown } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { Loader } from '@/components/custom/loader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PerformanceBar } from './trilha-performance-utils';

// Botão de expandir/colapsar cada área — estilo próprio por tema em vez do CardIcon
// padrão do Accordion compartilhado (que não dá pra recolorir pro laranja do playful).
function AreaToggle({ open, playful }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-transform duration-200 ease-out-strong',
        playful ? 'border-[var(--color-mission,#F99153)] bg-white text-[var(--color-mission,#F99153)]' : 'border-transparent bg-primary-lightest text-primary',
        open && 'rotate-180'
      )}
    >
      <FiChevronDown className="h-4 w-4" />
    </span>
  );
}

// Desempenho na trilha inteira (todos os capítulos), por área e matéria.
// `trilha.userLearningPathId`: id de qualquer capítulo (UserLearningPath) da trilha.
// As áreas e matérias já vêm ordenadas pela API (pior → melhor, sem dados por último).
export function TrilhaPerformanceModal({ open, onOpenChange, trilha }) {
  const client = useClient();
  const playful = usePlayfulStudent();
  const [openAreas, setOpenAreas] = useState([]);
  const [areas, setAreas] = useState(null); // null = carregando
  const [error, setError] = useState(false);
  const userLearningPathId = trilha?.userLearningPathId;

  // Busca a cada abertura (a API calcula na hora) e zera a expansão para não levá-la de uma trilha pra outra.
  useEffect(() => {
    if (!open || !userLearningPathId) return;
    let active = true;
    setOpenAreas([]);
    setAreas(null);
    setError(false);
    client
      .fetch(`user_learning_paths/${userLearningPathId}/performance`)
      .then(response => {
        if (!active) return;
        if (response?.error || response?.errors || !response?.data) setError(true);
        else setAreas(response.data.areas || []);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [client, open, userLearningPathId]);

  const toggleArea = name => setOpenAreas(prev => (prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]));

  const renderContent = () => {
    if (error) return <p className="py-6 text-center text-sm text-neutral-dark">Não foi possível carregar seu desempenho.</p>;
    if (!areas) return <Loader />;
    if (areas.length === 0) return <p className="py-6 text-center text-sm text-neutral-dark">Responda as questões da trilha para ver seu desempenho aqui.</p>;

    return (
      <div className="flex min-w-0 flex-col gap-3">
        {areas.map(area => {
          const isOpen = openAreas.includes(area.area);
          return (
            <div
              key={area.area}
              className={cn('min-w-0 overflow-hidden rounded-[8px] border shadow-dropshadow-sm', playful ? 'border-[var(--color-mission-lighter,#FFA96B)]/50 bg-[var(--color-bg-cream,#FFFAF5)]' : 'border-neutral-lightest bg-white')}
            >
              <button
                type="button"
                onClick={() => toggleArea(area.area)}
                className="flex w-full items-start gap-3 px-5 py-4 text-left"
              >
                <div className="min-w-0 flex-1">
                  <span className={cn('mb-2 block font-bold', playful ? 'text-[#222731]' : 'text-primary-dark')}>{area.area}</span>
                  <PerformanceBar
                    percentage={area.percentage}
                    level={area.level}
                  />
                </div>
                <AreaToggle
                  open={isOpen}
                  playful={playful}
                />
              </button>

              {isOpen && (
                <div className="mx-5 border-t border-solid border-neutral-lightest px-0 py-5">
                  <ul className="space-y-2">
                    {(area.subjects || []).map(subject => (
                      <li
                        key={subject.subject}
                        className={cn('flex items-center gap-3 rounded-lg border p-3', playful ? 'border-neutral-100 bg-white' : 'border-neutral-lightest bg-white')}
                      >
                        <span className={cn('min-w-0 flex-1 truncate font-bold', playful ? 'text-[#222731]' : 'text-primary-dark')}>{subject.subject}</span>
                        <PerformanceBar
                          percentage={subject.percentage}
                          level={subject.level}
                          compact
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        size="lg"
        className="overflow-x-hidden"
      >
        <DialogHeader>
          <DialogTitle className={playful ? 'text-[#222731]' : undefined}>Desempenho na Trilha</DialogTitle>
          {trilha?.title && <DialogDescription>{trilha.title}</DialogDescription>}
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
